import React, { useEffect, useState } from 'react';
import { Modal, BlockStack, InlineStack, Text, Select, TextField, Banner, Spinner } from '@shopify/polaris';
import { useAuthFetch } from '../AuthComponent';
import { useStore } from '../StoreContext';
import WarehouseSearchBox from '../WarehouseSearchBox';
import { variantLabel } from './overstockHelpers';

export default function PrintLabelModal({ open, onClose, api, warehouse, toast, onPrinted }) {
  const { activeStore } = useStore();
  const rawFetch = useAuthFetch();
  const [product, setProduct] = useState(null);        // {product_gid, matched_variant_gid}
  const [detail, setDetail] = useState(null);          // {title, variants}
  const [variantGid, setVariantGid] = useState('');
  const [qty, setQty] = useState('');
  const [copies, setCopies] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setProduct(null); setDetail(null); setVariantGid(''); setQty(''); setCopies('1'); setError(''); };

  useEffect(() => {
    if (!product) return;
    let alive = true;
    setDetail(null);
    api(`/api/portal/warehouse/product/${encodeURIComponent(product.product_gid)}`)
      .then(d => {
        if (!alive) return;
        setDetail(d);
        const vs = d.variants || [];
        const pick = vs.find(v => v.variant_gid === product.matched_variant_gid) || (vs.length === 1 ? vs[0] : null);
        setVariantGid(pick ? pick.variant_gid : '');
      })
      .catch(e => alive && setError(e.message));
    return () => { alive = false; };
  }, [product, api]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const j = await api('/api/rfid/labels', { method: 'POST', body: {
        variant_gid: variantGid,
        qty_units: qty === '' ? null : Number(qty),
        copies: Math.max(1, Math.min(50, Number(copies) || 1)),
      } });
      window.open(j.pdf_url, '_blank', 'noopener,noreferrer');
      toast('ok', `${j.box_ids.length} label${j.box_ids.length === 1 ? '' : 's'} created (#${j.box_ids[0]}${j.box_ids.length > 1 ? `–#${j.box_ids[j.box_ids.length - 1]}` : ''})`);
      onPrinted?.();
      reset();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const variants = detail?.variants || [];
  const chosen = variants.find(v => v.variant_gid === variantGid);

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title={`Print box labels · ${warehouse?.name || ''}`}
      primaryAction={{ content: 'Create labels & open PDF', onAction: submit, loading: busy, disabled: !variantGid || busy }}
      secondaryActions={[{ content: 'Cancel', onAction: () => { reset(); onClose(); } }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error && <Banner tone="critical">{error}</Banner>}
          {!product && (
            <WarehouseSearchBox
              storeKey={activeStore}
              shopifyLocationId={warehouse.shopify_location_id}
              apiFetch={rawFetch}
              onProductPicked={setProduct}
              onError={(msg) => setError(msg)}
            />
          )}
          {product && !detail && !error && <InlineStack align="center"><Spinner size="small" /></InlineStack>}
          {detail && (
            <BlockStack gap="300">
              <InlineStack gap="200" blockAlign="center">
                <Text as="p" variant="headingSm">{detail.title}</Text>
                <button type="button" className="overstock-tab" onClick={reset}>change product</button>
              </InlineStack>
              <Select
                label="Variant"
                options={variants.map(v => ({ label: `${variantLabel(v)} · ${v.sku || 'no SKU'}`, value: v.variant_gid }))}
                value={variantGid}
                placeholder="Choose a variant"
                onChange={setVariantGid}
              />
              {chosen && <Text as="p" tone="subdued">SKU <span className="overstock-mono">{chosen.sku || '—'}</span> · on hand {chosen.on_hand ?? '—'}</Text>}
              <InlineStack gap="300">
                <TextField label="Units per box (optional)" type="number" min={0} value={qty} onChange={setQty} autoComplete="off" />
                <TextField label="Copies" type="number" min={1} max={50} value={copies} onChange={setCopies} autoComplete="off" helpText="One box per copy, max 50" />
              </InlineStack>
            </BlockStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
